import React from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatDisplayDate } from '../../shared/heatmapViewUtils';
import './DailyTrendChart.css';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="daily-trend-tooltip" data-testid="daily-trend-tooltip">
        <p className="tooltip-date">{formatDisplayDate(label)}</p>
        {payload.map((entry, idx) => (
          <p key={`entry-${idx}`} className="tooltip-entry" style={{ color: entry.color }}>
            {entry.name}: <strong>{entry.value?.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string
};

/**
 * Bar/Area chart showing daily interaction and unique session volumes.
 */
export default function DailyTrendChart({ daily = [], height = 260, title = 'Daily Interaction Trends' }) {
  if (!Array.isArray(daily) || daily.length === 0) {
    return (
      <div className="daily-trend-card empty" data-testid="daily-trend-chart-empty">
        <h4 className="daily-trend-title">{title}</h4>
        <div className="daily-trend-empty">No daily data available for this range</div>
      </div>
    );
  }

  const formattedData = daily.map((d) => ({
    ...d,
    displayDate: d.date ? d.date.slice(5) : '' // MM-DD
  }));

  return (
    <div className="daily-trend-card" data-testid="daily-trend-chart">
      <h4 className="daily-trend-title">{title}</h4>
      <div className="daily-chart-wrapper" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={formattedData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={{ stroke: '#cbd5e1' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={{ stroke: '#cbd5e1' }}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              wrapperStyle={{ paddingBottom: '8px' }}
            />
            <Bar
              name="Interactions"
              dataKey="totalInteractions"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              name="Sessions"
              dataKey="uniqueSessions"
              fill="#10b981"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

DailyTrendChart.propTypes = {
  daily: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    totalInteractions: PropTypes.number,
    uniqueSessions: PropTypes.number
  })),
  height: PropTypes.number,
  title: PropTypes.string
};
